import { useEffect, useState } from "react";
import { Eye, KeyRound, Search, Trash2, X, CheckCircle2, XCircle, MailCheck, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { inputCls, Skeleton, btnPrimary } from "@/components/portal/ui";
import { AdminTitle } from "@/pages/admin/AdminLayout";

const LEVELS = ["Visitor", "New Soul", "Community", "Discipleship", "Serving", "Leadership"];
const ROLES = [["member", "Jemaat"], ["super_admin", "Super Admin"], ["pastor", "Pastor / Leadership"], ["finance", "Finance"],
  ["event_admin", "Event Admin"], ["community_leader", "Community Leader"], ["ministry_leader", "Ministry Leader"], ["content_admin", "Content Admin"]];

const EDIT_FIELDS = [
  ["name", "Nama Lengkap", "text"], ["email", "Email", "email"], ["gender", "Gender", "gender"],
  ["phone", "WhatsApp", "text"], ["birth_place", "Tempat Lahir", "text"], ["birth_date", "Tanggal Lahir", "date"],
  ["blood_type", "Gol. Darah", "text"], ["marital_status", "Status Pernikahan", "marital"], ["spouse_name", "Pasangan", "text"],
  ["children", "Anak", "text"], ["occupation", "Pekerjaan", "text"], ["education", "Pendidikan", "text"],
  ["address", "Alamat", "text"], ["city", "Kota", "text"], ["baptism_date", "Tgl. Baptis", "date"],
  ["baptism_place", "Tempat Baptis", "text"], ["emergency_name", "Kontak Darurat", "text"], ["emergency_phone", "No. Darurat", "text"],
];

function DetailDialog({ target, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/admin/members/${target.user_id}/detail`).then((r) => { setD(r.data); setForm(r.data); }).catch(() => setD({}));
  }, [target]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      EDIT_FIELDS.forEach(([k]) => { if ((form[k] ?? "") !== (d[k] ?? "")) body[k] = form[k] || null; });
      if (Object.keys(body).length === 0) { toast.message("Tidak ada perubahan"); setSaving(false); return; }
      await api.patch(`/admin/members/${target.user_id}`, body);
      toast.success("Biodata jemaat diperbarui");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="member-detail-dialog" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-3xl p-6 shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div><h2 className="text-lg font-black text-slate-900">Edit Biodata Jemaat</h2><p className="text-xs text-slate-500 mt-1">{target.name} · NIG {target.nig || "—"}</p></div>
          <button onClick={onClose} data-testid="member-detail-close" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        {!d ? <Skeleton className="h-64 mt-4" /> : (
          <>
            <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {EDIT_FIELDS.map(([k, label, type]) => (
                <label key={k} className="block" data-testid={`member-edit-${k}`}>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
                  {type === "gender" ? (
                    <select className={`${inputCls} mt-1`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`member-edit-input-${k}`}>
                      <option value="">—</option><option value="L">Pria</option><option value="P">Wanita</option>
                    </select>
                  ) : type === "marital" ? (
                    <select className={`${inputCls} mt-1`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`member-edit-input-${k}`}>
                      <option value="">—</option><option>Lajang</option><option>Menikah</option><option>Janda/Duda</option>
                    </select>
                  ) : (
                    <input type={type} className={`${inputCls} mt-1`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`member-edit-input-${k}`} />
                  )}
                </label>
              ))}
            </div>
            {d.ministries?.length > 0 && (
              <p className="mt-4 text-sm text-slate-600" data-testid="member-detail-ministries">Pelayanan: {d.ministries.map((m) => `${m.ministry_name} (${m.role})`).join(", ")}</p>
            )}
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} data-testid="member-edit-save" className={`${btnPrimary} flex-1`}>{saving ? "Menyimpan…" : "Simpan Biodata"}</button>
              <button onClick={onClose} className="px-5 rounded-xl border border-slate-200 text-sm font-bold text-slate-600">Batal</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
const sel = "border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-semibold bg-white outline-none focus:border-blue-500";

function MemberCard({ m, komsels, patch, onReset, onDetail, onRemove }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`member-card-${m.nig || m.user_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-slate-900 truncate">{m.name}</p>
          <p className="text-xs text-slate-500 truncate">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {m.nig
              ? <span className="text-[11px] font-bold tabular-nums bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">NIG {m.nig}</span>
              : <span className="text-[11px] font-bold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">Belum ada NIG</span>}
            <span className="text-[11px] font-semibold text-slate-500 bg-slate-50 rounded-full px-2 py-0.5">{m.gender === "L" ? "Pria" : m.gender === "P" ? "Wanita" : "Gender —"}</span>
            <span className="text-[11px] text-slate-400">{fmtDate(m.created_at, { day: "numeric", month: "short", year: "numeric" })}</span>
          </div>
        </div>
        <button onClick={() => patch(m, { active: !m.active })} data-testid={`member-active-${m.user_id}`} className={`shrink-0 text-[11px] font-bold rounded-full px-3 py-1 ${m.active ? "bg-emerald-500 text-white" : "bg-red-50 text-red-600"}`}>{m.active ? "Aktif" : "Nonaktif"}</button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5">
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">RC / TTS</span>
          <select className={`${sel} w-full mt-0.5`} value={m.komsel_id || ""} onChange={(e) => patch(m, { komsel_id: e.target.value })} data-testid={`member-komsel-${m.user_id}`}>
            <option value="">—</option>{komsels.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Lencana</span>
          <select className={`${sel} w-full mt-0.5`} value={m.level_override || ""} onChange={(e) => patch(m, { level_override: e.target.value })} data-testid={`member-level-${m.user_id}`}>
            <option value="">Otomatis</option>{LEVELS.map((l) => <option key={l}>{l}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Status Jemaat</span>
          <select className={`${sel} w-full mt-0.5`} value={m.member_status || "Jemaat"} onChange={(e) => patch(m, { member_status: e.target.value })} data-testid={`member-status-${m.user_id}`}>
            {["Simpatisan", "Jemaat", "Jemaat Tetap", "Pengerja", "Pindah"].map((l) => <option key={l}>{l}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Role</span>
          <select className={`${sel} w-full mt-0.5`} value={m.role === "admin" ? "super_admin" : m.role} onChange={(e) => patch(m, { role: e.target.value })} data-testid={`member-role-${m.user_id}`}>{ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </label>
        <label className="block col-span-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Pelayanan</span>
          <input defaultValue={m.ministry || ""} placeholder="Pelayanan" onBlur={(e) => e.target.value !== (m.ministry || "") && patch(m, { ministry: e.target.value })} className={`${sel} w-full mt-0.5`} data-testid={`member-ministry-${m.user_id}`} />
        </label>
      </div>
      <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
        <button onClick={() => onReset(m)} data-testid={`member-reset-${m.user_id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold rounded-xl bg-slate-100 text-slate-600 py-2 active:scale-95 transition"><KeyRound className="w-3.5 h-3.5" /> Reset</button>
        <button onClick={() => onDetail(m)} data-testid={`member-detail-${m.user_id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold rounded-xl bg-slate-100 text-slate-600 py-2 active:scale-95 transition"><Eye className="w-3.5 h-3.5" /> Biodata</button>
        <button onClick={() => onRemove(m)} data-testid={`member-delete-${m.user_id}`} className="inline-flex items-center justify-center rounded-xl bg-red-50 text-red-600 py-2 px-3 active:scale-95 transition"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function ResetDialog({ target, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/admin/members/${target.user_id}/reset-password`, { new_password: pw });
      toast.success(`Password baru untuk ${target.name}: ${pw} — sampaikan via WhatsApp`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="reset-password-dialog">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div><h2 className="text-lg font-black text-slate-900">Reset Password</h2><p className="text-xs text-slate-500 mt-1">{target.name} · {target.nig || target.email}</p></div>
          <button type="button" onClick={onClose} data-testid="reset-cancel-button" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <input data-testid="reset-new-password-input" className={`${inputCls} mt-4`} placeholder="Password baru (min. 6 karakter)" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} required />
        <p className="mt-2 text-[11px] text-slate-400">Password baru ditampilkan di notifikasi setelah disimpan. Sampaikan ke jemaat secara langsung/WhatsApp.</p>
        <button data-testid="reset-submit-button" disabled={loading} className={`${btnPrimary} w-full mt-4`}>{loading ? "Menyimpan…" : "Simpan Password Baru"}</button>
      </form>
    </div>
  );
}

export default function Members() {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [komsels, setKomsels] = useState([]);
  const [resets, setResets] = useState([]);
  const [resetTarget, setResetTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pending, setPending] = useState([]);

  const loadResets = () => api.get("/admin/password-resets").then((r) => setResets(r.data.filter((x) => x.status === "baru"))).catch(() => {});
  const loadPending = () => api.get("/admin/members/pending").then((r) => setPending(r.data)).catch(() => {});
  const loadMembers = () => api.get("/admin/members", { params: { q } }).then((r) => setList(r.data)).catch(() => {});

  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/members", { params: { q } }).then((r) => setList(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    api.get("/admin/r/komsels").then((r) => setKomsels(r.data));
    loadResets();
    loadPending();
  }, []);

  const approve = async (m) => {
    try {
      await api.post(`/admin/members/${m.user_id}/approve`);
      toast.success(`${m.name} disetujui & NIG diterbitkan`);
      loadPending();
      loadMembers();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const reject = async (m) => {
    if (!window.confirm(`Tolak pendaftaran ${m.name}? Akun akan dinonaktifkan.`)) return;
    try {
      await api.post(`/admin/members/${m.user_id}/reject`);
      toast.success(`Pendaftaran ${m.name} ditolak`);
      loadPending();
      loadMembers();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const dismissReset = async (r) => {
    await api.post(`/admin/password-resets/${r.id}/dismiss`);
    loadResets();
  };

  const patch = async (m, body) => {
    try {
      const { data } = await api.patch(`/admin/members/${m.user_id}`, body);
      setList((l) => l.map((x) => (x.user_id === m.user_id ? data : x)));
      toast.success("Data jemaat diperbarui");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Hapus akun ${m.name} beserta semua riwayatnya? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      await api.delete(`/admin/members/${m.user_id}`);
      setList((l) => l.filter((x) => x.user_id !== m.user_id));
      toast.success(`Akun ${m.name} dihapus`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-members">
      <AdminTitle eyebrow="Database" title="Jemaat">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="members-search-input" className={`${inputCls} !pl-11`} placeholder="Cari nama, email, NIG" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </AdminTitle>
      {pending.length > 0 && (
        <div className="mb-5 bg-blue-50 border border-blue-200 rounded-3xl p-5" data-testid="pending-approvals">
          <p className="text-xs font-bold uppercase tracking-widest text-blue-700">Persetujuan Jemaat Baru ({pending.length})</p>
          <div className="mt-3 space-y-2">
            {pending.map((m) => (
              <div key={m.user_id} className="flex flex-wrap items-center gap-3 bg-white rounded-2xl px-4 py-3" data-testid={`pending-row-${m.user_id}`}>
                <div className="flex-1 min-w-48">
                  <p className="text-sm font-bold text-slate-900">{m.name} <span className="text-xs font-semibold text-slate-500">· {m.email}</span></p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {m.email_verified
                      ? <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><MailCheck className="w-3 h-3" /> Email terverifikasi</span>
                      : <span className="text-[11px] font-bold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><MailWarning className="w-3 h-3" /> Email belum verifikasi</span>}
                    <span className="text-[11px] font-semibold text-slate-500">{m.gender === "L" ? "Pria" : m.gender === "P" ? "Wanita" : "Gender belum diisi"}</span>
                    {m.phone && <span className="text-[11px] font-semibold text-slate-500">· {m.phone}</span>}
                  </div>
                </div>
                <button onClick={() => approve(m)} data-testid={`pending-approve-${m.user_id}`} className="text-xs font-bold bg-emerald-600 text-white rounded-full px-4 py-2 hover:bg-emerald-700 active:scale-95 transition inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Setujui</button>
                <button onClick={() => reject(m)} data-testid={`pending-reject-${m.user_id}`} className="text-xs font-bold text-red-600 bg-red-50 rounded-full px-4 py-2 hover:bg-red-100 active:scale-95 transition inline-flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5" /> Tolak</button>
              </div>
            ))}
          </div>
        </div>
      )}
      {resets.length > 0 && (
        <div className="mb-5 bg-amber-50 border border-amber-200 rounded-3xl p-5" data-testid="password-reset-requests">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-700">Permintaan Reset Password ({resets.length})</p>
          <div className="mt-3 space-y-2">
            {resets.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 bg-white rounded-2xl px-4 py-3" data-testid={`reset-request-${r.id}`}>
                <div className="flex-1 min-w-40">
                  <p className="text-sm font-bold text-slate-900">{r.name} <span className="text-xs font-semibold text-slate-500">· {r.nig || r.email}</span></p>
                  {r.note && <p className="text-xs text-slate-500">"{r.note}"</p>}
                </div>
                <button onClick={() => setResetTarget(r)} data-testid={`reset-approve-${r.id}`} className="text-xs font-bold bg-blue-600 text-white rounded-full px-4 py-2 hover:bg-blue-700 active:scale-95 transition inline-flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Reset Password</button>
                <button onClick={() => dismissReset(r)} data-testid={`reset-dismiss-${r.id}`} className="text-xs font-bold text-slate-400 hover:text-slate-600">Tutup</button>
              </div>
            ))}
          </div>
        </div>
      )}
      {detail && <DetailDialog target={detail} onClose={() => setDetail(null)} onSaved={loadMembers} />}
      {resetTarget && <ResetDialog target={resetTarget} onClose={() => setResetTarget(null)} onDone={loadResets} />}
      {!list ? <Skeleton className="h-96" /> : (
        <>
          <div className="hidden md:block bg-white rounded-3xl shadow-sm border border-slate-100 overflow-x-auto" data-testid="members-table-wrap">
            <table className="w-full text-sm" data-testid="members-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <tr>{["Jemaat", "NIG", "Gender", "RC / TTS", "Lencana", "Status Jemaat", "Role", "Akun", "Terdaftar"].map((h) => <th key={h} className="px-5 py-4 font-bold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((m) => (
                <tr key={m.user_id} className="hover:bg-slate-50/60" data-testid={`member-row-${m.nig || m.user_id}`}>
                  <td className="px-5 py-4"><p className="font-bold text-slate-900">{m.name}</p><p className="text-xs text-slate-500">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p></td>
                  <td className="px-5 py-4 font-bold tabular-nums">{m.nig || <span className="text-[11px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">Belum</span>}</td>
                  <td className="px-5 py-4 text-slate-600">{m.gender === "L" ? "Pria" : m.gender === "P" ? "Wanita" : "—"}</td>
                  <td className="px-5 py-4">
                    <select className={sel} value={m.komsel_id || ""} onChange={(e) => patch(m, { komsel_id: e.target.value })} data-testid={`member-komsel-${m.user_id}`}>
                      <option value="">—</option>{komsels.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    <select className={sel} value={m.level_override || ""} onChange={(e) => patch(m, { level_override: e.target.value })} data-testid={`member-level-${m.user_id}`}>
                      <option value="">Otomatis</option>{LEVELS.map((l) => <option key={l}>{l}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    <select className={sel} value={m.member_status || "Jemaat"} onChange={(e) => patch(m, { member_status: e.target.value })} data-testid={`member-status-${m.user_id}`}>
                      {["Simpatisan", "Jemaat", "Jemaat Tetap", "Pengerja", "Pindah"].map((l) => <option key={l}>{l}</option>)}
                    </select>
                    <input defaultValue={m.ministry || ""} placeholder="Pelayanan" onBlur={(e) => e.target.value !== (m.ministry || "") && patch(m, { ministry: e.target.value })} className={`${sel} mt-1 w-28 block`} data-testid={`member-ministry-${m.user_id}`} />
                  </td>
                  <td className="px-5 py-4">
                    <select className={sel} value={m.role === "admin" ? "super_admin" : m.role} onChange={(e) => patch(m, { role: e.target.value })} data-testid={`member-role-${m.user_id}`}>{ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => patch(m, { active: !m.active })} data-testid={`member-active-${m.user_id}`} className={`text-[11px] font-bold rounded-full px-3 py-1 ${m.active ? "bg-emerald-500 text-white" : "bg-red-50 text-red-600"}`}>{m.active ? "Aktif" : "Nonaktif"}</button>
                      <button onClick={() => setResetTarget(m)} data-testid={`member-reset-${m.user_id}`} title="Reset password" className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 grid place-items-center hover:bg-blue-50 hover:text-blue-600 transition"><KeyRound className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setDetail(m)} data-testid={`member-detail-${m.user_id}`} title="Edit biodata" className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 grid place-items-center hover:bg-slate-200 transition"><Eye className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(m)} data-testid={`member-delete-${m.user_id}`} title="Hapus jemaat" className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 grid place-items-center hover:bg-red-50 hover:text-red-600 transition"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-xs text-slate-500">{fmtDate(m.created_at, { day: "numeric", month: "short", year: "numeric" })}</td>
                </tr>
              ))}
            </tbody>
          </table>
            {list.length === 0 && <p className="p-8 text-center text-slate-500">Tidak ada jemaat ditemukan.</p>}
          </div>
          <div className="md:hidden space-y-3" data-testid="members-card-list">
            {list.map((m) => (
              <MemberCard key={m.user_id} m={m} komsels={komsels} patch={patch} onReset={setResetTarget} onDetail={setDetail} onRemove={remove} />
            ))}
            {list.length === 0 && <p className="p-8 text-center text-slate-500 bg-white rounded-2xl border border-slate-100">Tidak ada jemaat ditemukan.</p>}
          </div>
        </>
      )}
    </div>
  );
}
